// hooks/useOrders.ts
import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Order, OrderUpdateData } from '../types/order';

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('order_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOrder = useCallback(async (orderId: string, updates: OrderUpdateData) => {
    try {
      setError(null);

      // Aggiorna l'ordine principale
      const { error } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;

      // Se lo stato è cambiato, aggiungi alla cronologia
      if (updates.status) {
        await supabase
          .from('order_status_history')
          .insert({
            order_id: orderId,
            status: updates.status,
            notes: updates.notes
          });
      }

      // Aggiorna lo stato locale
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, ...updates } : order
      ));

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  }, []);

  const deleteOrder = useCallback(async (orderId: string) => {
    try {
      setError(null);

      // Righe e storico si cancellano con l'ordine (FK ON DELETE CASCADE).
      // Il DB rifiuta la cancellazione di un ordine pagato.
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;

      // Aggiorna lo stato locale
      setOrders(prev => prev.filter(order => order.id !== orderId));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  }, []);

  const getOrderStats = useCallback(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.total_price, 0);
    const pendingOrders = orders.filter(order => order.status === 'pending').length;
    const paidOrders = orders.filter(order => order.payment_status === 'paid').length;

    return {
      totalOrders,
      totalRevenue,
      pendingOrders,
      paidOrders
    };
  }, [orders]);

  return {
    orders,
    loading,
    error,
    setError,
    fetchOrders,
    updateOrder,
    deleteOrder,
    getOrderStats
  };
};